import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, Star, Truck, Shield, Check, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { formatPrice } from '../lib/format';
import { isSaleActive } from '../lib/sale';
import { SaleCountdown } from '../components/SaleCountdown';
import ImageWithRetry from '../components/ImageWithRetry';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getProxiedImage = (imageUrl) => {
  if (!imageUrl) return null;
  if (imageUrl.includes('kalyantop.com.ua')) {
    return `${API}/image-proxy?url=${encodeURIComponent(imageUrl)}`;
  }
  return imageUrl;
};

const ProductPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, getLocalizedText, language } = useLanguage();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API}/products/${id}`);
        setProduct(response.data);
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-zinc-500" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <p className="text-zinc-500 text-xl mb-4">{t.common.error}</p>
          <Button onClick={() => navigate('/')} variant="outline" className="border-zinc-700 text-white">
            {t.common.back}
          </Button>
        </div>
      </div>
    );
  }

  // Combine main image with additional images
  const allImages = [product.image, ...(product.images || [])].filter(Boolean);

  const handleAddToCart = () => {
    const cartProduct = {
      ...product,
      image: getProxiedImage(product.image)
    };
    addToCart(cartProduct);
  };

  const nextImage = () => {
    setSelectedImage((prev) => (prev + 1) % allImages.length);
  };

  const prevImage = () => {
    setSelectedImage((prev) => (prev - 1 + allImages.length) % allImages.length);
  };

  return (
    <div className="bg-black py-4 md:py-8 px-3 md:px-4">
      <div className="container mx-auto max-w-6xl">
        {/* Back Button */}
        <Button
          variant="ghost"
          className="text-zinc-400 hover:text-white mb-4 md:mb-6"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t.common.back}
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-12">
          {/* Image Gallery */}
          <div className="relative">
            {/* Main Image */}
            <div className="aspect-square bg-zinc-900 rounded-xl overflow-hidden relative">
              <ImageWithRetry
                src={getProxiedImage(allImages[selectedImage])}
                alt={getLocalizedText(product.name)}
                className={`w-full h-full ${product.category === 'set' ? 'object-contain p-3' : 'object-cover'}`}
                fallbackText={product.brand || 'Product'}
                fallbackSize="400x400"
              />
              
              {/* Navigation arrows */}
              {allImages.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-colors"
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-colors"
                  >
                    <ChevronRight className="h-6 w-6" />
                  </button>
                </>
              )}
              
              {!product.inStock && (
                <Badge className="absolute top-4 left-4 bg-red-600 text-white">
                  {t.products.outOfStock}
                </Badge>
              )}
              
              {isSaleActive(product) && (
                <Badge className="absolute top-4 right-4 bg-gradient-to-r from-orange-500 to-red-500 text-white">
                  {product.salePrice ? `-${Math.round((1 - product.salePrice / product.price) * 100)}%` : 'Sale'}
                </Badge>
              )}
            </div>

            {/* Thumbnail Gallery */}
            {allImages.length > 1 && (
              <div className="flex gap-2 mt-3 overflow-x-auto pb-2">
                {allImages.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`flex-shrink-0 w-16 h-16 md:w-20 md:h-20 bg-zinc-900 rounded-lg overflow-hidden border-2 transition-colors ${
                      selectedImage === index ? 'border-white' : 'border-transparent hover:border-zinc-700'
                    }`}
                  >
                    <ImageWithRetry
                      src={getProxiedImage(img)}
                      alt={`${getLocalizedText(product.name)} - ${index + 1}`}
                      className="w-full h-full object-cover"
                      fallbackText={`${index + 1}`}
                      fallbackSize="80x80"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="flex flex-col">
            {/* Brand */}
            {product.brand && (
              <span className="text-sm font-medium text-zinc-500 uppercase tracking-wider mb-2">
                {product.brand}
              </span>
            )}

            {/* Name */}
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-3 md:mb-4">
              {getLocalizedText(product.name)}
            </h1>

            {/* Rating */}
            {product.rating && (
              <div className="flex items-center gap-2 mb-3 md:mb-4">
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`h-4 w-4 md:h-5 md:w-5 ${
                        i < Math.floor(product.rating)
                          ? 'fill-yellow-500 text-yellow-500'
                          : 'text-zinc-600'
                      }`}
                    />
                  ))}
                </div>
                <span className="text-zinc-400 text-sm">({product.rating})</span>
              </div>
            )}

            {/* Price */}
            <div className="mb-4 md:mb-6">
              {isSaleActive(product) ? (
                <div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-3xl md:text-4xl font-bold text-red-500" data-testid="product-sale-price">
                      {formatPrice(product.salePrice)}
                    </span>
                    <span className="text-xl md:text-2xl text-zinc-500 line-through">
                      {formatPrice(product.price)}
                    </span>
                    <span className="text-lg md:text-xl text-red-400/80">HUF</span>
                  </div>
                  {product.saleEndDate && (
                    <SaleCountdown saleEndDate={product.saleEndDate} language={language} className="mt-2 text-sm" />
                  )}
                </div>
              ) : (
                <div>
                  <span className="text-3xl md:text-4xl font-bold text-white">
                    {formatPrice(product.price)}
                  </span>
                  <span className="text-lg md:text-xl text-zinc-500 ml-2">HUF</span>
                </div>
              )}
            </div>

            {/* Foreign warehouse shipping notice for Hookah/Set */}
            {(product.category === 'hookah' || product.category === 'set') && (
              <div className="mb-6 text-sm text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-lg px-4 py-3" data-testid="product-shipping-notice">
                {language === 'hu'
                  ? 'A vízipipák és a teljes készletű vízipipák külföldi raktárból érkeznek, ezért szállításuk általában 5-7 munkanapot vesz igénybe. Vegyes rendelés esetén (vízipipa és kiegészítők) a csomagot akár a vízipipa beérkezése után küldjük, vagy a kiegészítőket azonnal elküldjük, és a vízipipát a beérkezése után külön postázzuk.'
                  : 'Hookahs and complete hookah sets are shipped from a foreign warehouse, so delivery usually takes 5-7 business days. For mixed orders (hookah and accessories), we may send the package once the hookah arrives, or ship the accessories immediately and send the hookah separately after it arrives.'}
              </div>
            )}

            {/* Specifications Table for Hookah/Set */}
            {(product.category === 'hookah' || product.category === 'set') && product.specs && (
              <div className="mb-6 md:mb-8">
                <h3 className="text-base md:text-lg font-semibold text-white mb-3">{language === 'hu' ? 'Specifikációk' : 'Specifications'}</h3>
                <div className="bg-zinc-900 rounded-lg overflow-hidden border border-zinc-800">
                  <table className="w-full text-sm">
                    <tbody>
                      {product.specs.height && (
                        <tr className="border-b border-zinc-800">
                          <td className="px-4 py-3 text-zinc-400 font-medium w-1/2">{language === 'hu' ? 'Magasság' : 'Height'}</td>
                          <td className="px-4 py-3 text-white">{product.specs.height}</td>
                        </tr>
                      )}
                      {product.specs.brand && (
                        <tr className="border-b border-zinc-800">
                          <td className="px-4 py-3 text-zinc-400 font-medium">{language === 'hu' ? 'Márka' : 'Brand'}</td>
                          <td className="px-4 py-3 text-white">{product.specs.brand}</td>
                        </tr>
                      )}
                      {product.specs.material && (
                        <tr className="border-b border-zinc-800">
                          <td className="px-4 py-3 text-zinc-400 font-medium">{language === 'hu' ? 'Anyag' : 'Material'}</td>
                          <td className="px-4 py-3 text-white">{product.specs.material}</td>
                        </tr>
                      )}
                      {product.specs.water_tank_connection && (
                        <tr className="border-b border-zinc-800">
                          <td className="px-4 py-3 text-zinc-400 font-medium">Víztartály csatlakozás</td>
                          <td className="px-4 py-3 text-white">{product.specs.water_tank_connection}</td>
                        </tr>
                      )}
                      {product.specs.hose_type && (
                        <tr>
                          <td className="px-4 py-3 text-zinc-400 font-medium">Gyári csövek tipusa</td>
                          <td className="px-4 py-3 text-white">{product.specs.hose_type}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Basic Specs (fallback for products without detailed specs) */}
            {!(product.specs && (product.specs.height || product.specs.brand || product.specs.material)) && (
              <div className="grid grid-cols-2 gap-3 md:gap-4 mb-6 md:mb-8">
                {product.height && (
                  <div className="bg-zinc-900 rounded-lg p-3 md:p-4">
                    <span className="text-xs md:text-sm text-zinc-500">{t.products.height}</span>
                    <p className="text-white font-semibold text-sm md:text-base">{product.height}</p>
                  </div>
                )}
                {product.brand && (
                  <div className="bg-zinc-900 rounded-lg p-3 md:p-4">
                    <span className="text-xs md:text-sm text-zinc-500">{t.products.brand}</span>
                    <p className="text-white font-semibold text-sm md:text-base">{product.brand}</p>
                  </div>
                )}
              </div>
            )}

            {/* Add to Cart */}
            <div className="flex gap-3 md:gap-4 mb-6 md:mb-8">
              <Button
                size="lg"
                className="flex-1 bg-white text-black hover:bg-zinc-200 font-semibold py-5 md:py-6 text-sm md:text-base"
                onClick={handleAddToCart}
                disabled={!product.inStock}
                data-testid="add-to-cart-button"
              >
                <ShoppingCart className="mr-2 h-4 w-4 md:h-5 md:w-5" />
                {t.products.addToCart}
              </Button>
            </div>

            {/* Features */}
            <div className="space-y-2 md:space-y-3 text-sm md:text-base">
              <div className="flex items-center gap-3 text-zinc-400">
                <Truck className="h-4 w-4 md:h-5 md:w-5 flex-shrink-0" />
                <span>{t.footer.deliveryDesc}</span>
              </div>
              <div className="flex items-center gap-3 text-zinc-400">
                <Shield className="h-4 w-4 md:h-5 md:w-5 flex-shrink-0" />
                <span>{t.footer.qualityDesc}</span>
              </div>
              {product.inStock && product.category !== 'hookah' && product.category !== 'set' && (
                <div className="flex items-center gap-3 text-green-500">
                  <Check className="h-4 w-4 md:h-5 md:w-5 flex-shrink-0" />
                  <span>{t.products.inStock}</span>
                </div>
              )}
            </div>

            {/* Description - moved to bottom */}
            <div className="mt-6 md:mt-8 pt-6 border-t border-zinc-800">
              <h3 className="text-base md:text-lg font-semibold text-white mb-3">{t.products.description}</h3>
              <p className="text-zinc-400 leading-relaxed text-sm md:text-base text-left whitespace-pre-line">
                {getLocalizedText(product.description)}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductPage;
